import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, Upload, Clock, CheckCircle, XCircle, ChevronRight, Star, Target, Award, Zap, Sprout, GraduationCap, MapPin, Shield, Flag, Medal, Globe, Flame, Building, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STUDENT_LEVELS = [
  { id: "beginner", name: "Beginner", description: "Just starting out", icon: Sprout },
  { id: "advanced", name: "Advanced", description: "Play regularly, want to improve", icon: Flame },
  { id: "professional", name: "Professional", description: "Compete or go pro", icon: Trophy }
];

const SPORTS = [
  "Cricket", "Football", "Badminton", "Tennis", "Swimming", 
  "Boxing", "Hockey", "Basketball", "Volleyball", "Athletics"
];

function StudentLevelPage({ user }) {
  const [levelStatus, setLevelStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState('');
  const [proofFiles, setProofFiles] = useState([null]); // Array of files for multiple uploads
  const [proofDescription, setProofDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchLevelStatus();
  }, []);

  const fetchLevelStatus = async () => {
    try {
      const response = await axios.get(`${API}/students/level-status`, { withCredentials: true });
      setLevelStatus(response.data);
    } catch (error) {
      console.error('Error fetching level status:', error);
      toast.error('Failed to load level status');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLevelIndex = (sport) => {
    const currentLevel = levelStatus?.sport_levels?.[sport.toLowerCase()] || 'beginner';
    return STUDENT_LEVELS.findIndex(l => l.id === currentLevel);
  };

  const getNextLevel = (sport) => {
    const currentIdx = getCurrentLevelIndex(sport);
    if (currentIdx < STUDENT_LEVELS.length - 1) {
      return STUDENT_LEVELS[currentIdx + 1];
    }
    return null;
  };

  const handleSubmitProof = async () => {
    if (!selectedSport || !proofDescription) {
      toast.error('Please select a sport and provide proof description');
      return;
    }

    const nextLevel = getNextLevel(selectedSport);
    if (!nextLevel) {
      toast.error('You have reached the maximum level');
      return;
    }

    setSubmitting(true);
    try {
      // Filter out null files
      const uploadedUrls = proofFiles.filter(f => f !== null).map((_, idx) => `uploaded_${Date.now()}_${idx}`);
      
      await axios.post(`${API}/students/level-proof/submit`, {
        sport: selectedSport.toLowerCase(),
        target_level: nextLevel.id,
        proof_urls: uploadedUrls.length > 0 ? uploadedUrls : null,
        description: proofDescription
      }, { withCredentials: true });

      toast.success('Level progression proof submitted successfully!');
      setShowUploadModal(false);
      setSelectedSport('');
      setProofDescription('');
      setProofFiles([null]);
      fetchLevelStatus();
    } catch (error) {
      console.error('Error submitting proof:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit proof');
    } finally {
      setSubmitting(false);
    }
  };

  const addFileSlot = () => {
    if (proofFiles.length < 5) {
      setProofFiles([...proofFiles, null]);
    }
  };

  const removeFileSlot = (index) => {
    const newFiles = proofFiles.filter((_, idx) => idx !== index);
    setProofFiles(newFiles.length > 0 ? newFiles : [null]);
  };

  const updateFile = (index, file) => {
    const newFiles = [...proofFiles];
    newFiles[index] = file;
    setProofFiles(newFiles);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="student-level-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            Progress Tracker
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="level-page-title"
          >
            Your <span className="gold-gradient">Level</span> Progress
          </h1>
          <p className="text-white/60 mt-2">Track your athletic journey and submit proof to level up</p>
        </div>

        {/* Current Levels Overview */}
        <div className="bg-dark-surface rounded-xl border border-premium-gold/30/50 p-6 mb-8" data-testid="levels-overview">
          <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
            <Trophy className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
            Your Current Levels
          </h2>
          
          {Object.keys(levelStatus?.sport_levels || {}).length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-deep-dark rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-white/50">No sport levels recorded yet. Start by selecting a sport below!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(levelStatus.sport_levels).map(([sport, level]) => {
                const levelInfo = STUDENT_LEVELS.find(l => l.id === level) || STUDENT_LEVELS[0];
                const LevelIcon = levelInfo.icon;
                const levelIdx = STUDENT_LEVELS.findIndex(l => l.id === level);
                const progress = ((levelIdx + 1) / STUDENT_LEVELS.length) * 100;
                
                return (
                  <div key={sport} className="bg-deep-dark/50 p-4 rounded-xl border border-premium-gold/30" data-testid={`sport-level-${sport}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold capitalize text-white">{sport}</span>
                      <LevelIcon className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                    </div>
                    <p className="text-premium-gold font-bold text-sm">{levelInfo.name}</p>
                    <div className="mt-3 bg-deep-dark rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-premium-gold to-yellow-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-white/50 mt-2">{levelIdx + 1} / {STUDENT_LEVELS.length} levels</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pending Progressions */}
        {levelStatus?.pending_progressions?.length > 0 && (
          <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-6 mb-8" data-testid="pending-progressions">
            <h2 className="text-2xl font-bold mb-4 text-amber-400 flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
              <Clock className="w-6 h-6" strokeWidth={1.5} />
              Pending Reviews
            </h2>
            <div className="space-y-3">
              {levelStatus.pending_progressions.map((prog) => (
                <div key={prog.progression_id} className="bg-dark-surface p-4 rounded-xl border border-amber-500/20 flex items-center justify-between">
                  <div>
                    <p className="font-semibold capitalize text-white">{prog.sport}</p>
                    <p className="text-sm text-white/60">
                      {prog.from_level} <ChevronRight className="inline w-4 h-4" /> {prog.to_level}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-sm font-medium rounded-full">
                    Under Review
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Level History */}
        {levelStatus?.level_history?.length > 0 && (
          <div className="bg-dark-surface rounded-xl border border-premium-gold/30/50 p-6 mb-8" data-testid="level-history">
            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
              <Award className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
              Level History
            </h2>
            <div className="space-y-3">
              {levelStatus.level_history.slice().reverse().map((entry, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-deep-dark/50 rounded-xl border border-premium-gold/30/30">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold capitalize text-white">{entry.sport}</p>
                    <p className="text-sm text-white/60">
                      Promoted from <span className="font-medium text-white/80">{entry.from_level}</span> to{' '}
                      <span className="font-medium text-premium-gold">{entry.to_level}</span>
                    </p>
                  </div>
                  <span className="text-xs text-white/40">
                    {new Date(entry.date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Level Up Section */}
        <div className="bg-dark-surface text-white rounded-lg p-8 mb-8 border-l-4 border-premium-gold" data-testid="level-up-section">
          <div className="flex items-start gap-4">
            <Trophy className="w-8 h-8 text-premium-gold flex-shrink-0" strokeWidth={1.5} />
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
                Ready to Level Up?
              </h2>
              <p className="text-soft-lavender mb-6">
                Submit proof of your achievements to progress to the next level. Your coach will review and approve your advancement.
              </p>
              
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-transparent border border-premium-gold text-premium-gold font-semibold tracking-wider px-8 py-3 rounded-lg transition-all flex items-center gap-2 hover:bg-premium-gold hover:text-deep-dark"
                data-testid="submit-proof-btn"
              >
                <Upload className="w-5 h-5" strokeWidth={1.5} />
                Submit Level Proof
              </button>
            </div>
          </div>
        </div>

        {/* Level Progression Chart */}
        <div className="bg-dark-surface rounded-xl border border-premium-gold/20 p-6" data-testid="level-chart">
          <h2 className="text-2xl font-bold mb-6 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
            Level Progression Path
          </h2>
          <div className="flex flex-wrap gap-2 items-center justify-center">
            {STUDENT_LEVELS.map((level, idx) => {
              const LevelIcon = level.icon;
              return (
                <React.Fragment key={level.id}>
                  <div className="flex flex-col items-center p-4 bg-dark-surface rounded-xl min-w-[110px] border border-premium-gold">
                    <LevelIcon className="w-8 h-8 text-premium-gold mb-2" strokeWidth={1.5} />
                    <span className="text-xs font-semibold text-white text-center">{level.name}</span>
                  </div>
                  {idx < STUDENT_LEVELS.length - 1 && (
                    <ChevronRight className="w-5 h-5 text-premium-gold/50 flex-shrink-0" strokeWidth={1.5} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="upload-modal">
          <div className="bg-dark-surface rounded-xl max-w-lg w-full p-6 border border-premium-gold/20">
            <h3 className="text-2xl font-bold mb-4 text-white" style={{ fontFamily: 'Fraunces, serif' }}>
              Submit Level Proof
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">Select Sport</label>
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="w-full p-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:ring-2 focus:ring-premium-gold focus:border-premium-gold transition-all"
                  data-testid="sport-select"
                >
                  <option value="">Choose a sport...</option>
                  {SPORTS.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>

              {selectedSport && (
                <div className="bg-deep-dark/50 p-4 rounded-lg border border-premium-gold/30/30">
                  <p className="text-sm text-white/60">
                    Current Level: <span className="font-bold text-white">
                      {STUDENT_LEVELS[getCurrentLevelIndex(selectedSport)]?.name || 'Beginner'}
                    </span>
                  </p>
                  <p className="text-sm text-white/60 mt-1">
                    Next Level: <span className="font-bold text-premium-gold">
                      {getNextLevel(selectedSport)?.name || 'Maximum Reached'}
                    </span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Proof Description
                </label>
                <textarea
                  value={proofDescription}
                  onChange={(e) => setProofDescription(e.target.value)}
                  placeholder="Describe your achievement (e.g., competition name, date, position, certificate details)"
                  className="w-full p-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:ring-2 focus:ring-premium-gold focus:border-premium-gold transition-all h-32 resize-none"
                  data-testid="proof-description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Upload Certificates/Proof (Optional)
                </label>
                <div className="space-y-3">
                  {proofFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <div className="border-2 border-dashed border-premium-gold/30 rounded-lg p-4 text-center hover:border-premium-gold transition-colors cursor-pointer bg-deep-dark/30">
                        <input
                          type="file"
                          onChange={(e) => updateFile(index, e.target.files[0])}
                          className="hidden"
                          id={`proof-upload-${index}`}
                          accept="image/*,.pdf"
                        />
                        <label htmlFor={`proof-upload-${index}`} className="cursor-pointer flex items-center justify-center gap-3">
                          <Upload className="w-6 h-6 text-premium-gold" strokeWidth={1.5} />
                          <div className="flex-1 text-left">
                            <p className="text-sm font-semibold text-white">Document {index + 1}</p>
                            <p className="text-xs text-soft-lavender">
                              {file ? file.name : 'Click to upload certificate or proof'}
                            </p>
                          </div>
                        </label>
                        {proofFiles.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFileSlot(index)}
                            className="absolute top-2 right-2 w-6 h-6 bg-deep-dark hover:bg-premium-gold/20 rounded-full flex items-center justify-center transition-colors"
                          >
                            <X className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {proofFiles.length < 5 && (
                    <button
                      type="button"
                      onClick={addFileSlot}
                      className="w-full py-3 bg-transparent border border-premium-gold/30 text-premium-gold rounded-lg hover:bg-premium-gold/10 transition-all flex items-center justify-center gap-2"
                    >
                      <Upload className="w-4 h-4" strokeWidth={1.5} />
                      Add another document
                    </button>
                  )}
                  
                  <p className="text-xs text-soft-lavender">
                    Maximum 5 documents. Accepts images and PDFs.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setProofFiles([null]);
                  setSelectedSport('');
                  setProofDescription('');
                }}
                className="flex-1 bg-transparent border border-premium-gold/30 text-white hover:bg-deep-dark/50 font-semibold py-3 rounded-lg transition-all"
                data-testid="cancel-upload-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitProof}
                disabled={submitting || !selectedSport || !proofDescription}
                className="flex-1 bg-premium-gold hover:opacity-90 text-deep-dark font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-upload-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Proof'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentLevelPage;
